import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolvePrivateImage, formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, MessageCircle, Upload, Loader2 } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !order) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${order.id}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("order.proofUploaded"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const waLink = () => {
    if (!order) return null;
    const num = (settings.whatsapp_number || "").replace(/[^\d]/g, "");
    if (!num) return null;
    const zh = !i18n.language?.startsWith("en");
    const msg = zh
      ? `您好，我的订单 #${order.order_no}，金额 ${formatRM(order.total)}，稍后上传付款凭证。`
      : `Hello, my order #${order.order_no}, total ${formatRM(order.total)}. I will upload payment proof shortly.`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16" data-testid="order-success-page">
      <div className="text-center">
        <div className="mx-auto h-20 w-20 rounded-full bg-[#00868e]/10 flex items-center justify-center">
          <CheckCircle2 size={40} className="text-[#00868e]" />
        </div>
        <h1 className="mt-6 font-serif-cn text-4xl text-[#003645] font-semibold">{t("order.orderSuccess")}</h1>
        <p className="mt-4 text-[#4a6e77] leading-relaxed">{t("order.orderSuccessDesc")}</p>
        {order && (
          <div className="mt-6 text-sm text-[#4a6e77]" data-testid="order-success-info">
            <div>#{order.order_no}</div>
            {(order.points_used || 0) > 0 && (
              <div className="mt-2 space-y-0.5 text-xs">
                <div>{t("common.subtotal")}: {formatRM(order.total)}</div>
                <div className="text-[#00868e]">- {t("points.title")}: {formatRM(order.points_used)}</div>
              </div>
            )}
            <div className="text-[#005870] font-semibold text-xl mt-1">{formatRM(order.cash_paid ?? order.total)}</div>
            {order.payment_method === "stripe" && order.stripe_payment_status === "paid" && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-[#00868e] text-sm font-medium" data-testid="order-stripe-paid">
                ✓ {i18n.language?.startsWith("en") ? "Paid via Stripe" : "已通过 Stripe 支付"}
              </div>
            )}
          </div>
        )}
      </div>

      {order && order.status !== "confirmed" && order.payment_method !== "stripe" && (
        <div className="mt-10 rounded-lg border border-[#e5ebeb] bg-white p-6" data-testid="success-proof-section">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">payment proof</div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
            <div><div className="text-[#4a6e77]">{t("checkout.bankName")}</div><div className="text-[#003645] font-medium">{settings.bank_name}</div></div>
            <div><div className="text-[#4a6e77]">{t("checkout.accountName")}</div><div className="text-[#003645] font-medium">{settings.bank_account_name}</div></div>
            <div><div className="text-[#4a6e77]">{t("checkout.accountNumber")}</div><div className="text-[#003645] font-medium tracking-wider">{settings.bank_account_number}</div></div>
          </div>
          {order.payment_proof ? (
            <div className="mt-5">
              <div className="text-sm text-[#4a6e77] mb-2">{t("order.proofUploaded")}</div>
              <a href={resolvePrivateImage(order.payment_proof)} target="_blank" rel="noopener noreferrer">
                <img src={resolvePrivateImage(order.payment_proof)} alt="proof" className="max-h-56 rounded-md border border-[#e5ebeb]" data-testid="success-proof-preview" />
              </a>
              <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#005870] hover:text-[#00868e] cursor-pointer">
                <Upload size={14} /> {t("order.reuploadProof")}
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="success-reupload-input" />
              </label>
            </div>
          ) : (
            <label className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] cursor-pointer transition-colors duration-200" data-testid="success-upload-proof-btn">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {t("order.uploadProof")}
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="success-upload-input" />
            </label>
          )}
        </div>
      )}

      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link to={`/orders/${id}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200" data-testid="goto-order-btn">
          {t("order.goToOrder")} <ArrowRight size={14} />
        </Link>
        {waLink() && (
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-medium hover:opacity-90 transition-opacity duration-200" data-testid="wa-contact-btn">
            <MessageCircle size={16} /> {t("order.contactWa")}
          </a>
        )}
      </div>
    </div>
  );
}
